/**
 * The grant editor (SPEC §9.1, §7.1, §10).
 *
 * "One row per granted department with **All reports** / **Specific reports**
 * and a checklist when specific."
 *
 * Three rules are visible in the interface rather than only in the API:
 *
 * * **All reports is the default**, because it is the normal case (§7.1) and
 *   the one that means a report shipped next month appears without an admin
 *   edit.
 * * Choosing **All reports** hides the checklist entirely and sends no slugs.
 *   The server refuses `all` carrying `report_slugs`; keeping a disabled list
 *   on screen would suggest the two combine.
 * * **Specific reports with nothing ticked cannot be submitted.** The server
 *   refuses that too — it grants nothing, which nobody means.
 *
 * The client checks are courtesy. The contract is the server's, and both
 * rejections are asserted against it in `test_admin_grants.py` and
 * `test_admin_api.py`.
 *
 * A restricted report appears in the checklist and only there: naming it in a
 * `selected` grant is the one way to reach it (§7.1).
 */

import { useState } from 'react'

import type { AdminReportRow, Grant, GrantInput } from '../../lib/adminTypes'
import type { DraftGrant } from '../../lib/grants'
import { draftProblems, toGrantInputs } from '../../lib/grants'
import type { DepartmentSummary } from '../../lib/types'
import { Badge, Button } from './ui'

function toDraft(grants: readonly Grant[]): DraftGrant[] {
  return grants.map((grant) => ({
    department: grant.department,
    scope: grant.scope,
    slugs: [...grant.report_slugs],
  }))
}

export function GrantEditor({
  grants,
  departments,
  reports,
  saving,
  onSave,
}: {
  grants: readonly Grant[]
  departments: readonly DepartmentSummary[]
  reports: readonly AdminReportRow[]
  saving: boolean
  onSave: (grants: readonly GrantInput[]) => void
}) {
  const [draft, setDraft] = useState<DraftGrant[]>(() => toDraft(grants))
  const problems = draftProblems(draft)
  const granted = new Set(draft.map((grant) => grant.department))
  const available = departments.filter((department) => !granted.has(department.key))

  function update(index: number, next: Partial<DraftGrant>): void {
    setDraft((current) =>
      current.map((grant, position) => (position === index ? { ...grant, ...next } : grant)),
    )
  }

  function toggleSlug(index: number, slug: string): void {
    setDraft((current) =>
      current.map((grant, position) =>
        position === index
          ? {
              ...grant,
              slugs: grant.slugs.includes(slug)
                ? grant.slugs.filter((value) => value !== slug)
                : [...grant.slugs, slug],
            }
          : grant,
      ),
    )
  }

  return (
    <div className="space-y-3">
      {draft.length === 0 && (
        <p className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          This role grants nothing. Add a department below.
        </p>
      )}

      {draft.map((grant, index) => {
        const department = departments.find((item) => item.key === grant.department)
        const inDepartment = reports.filter(
          (report) => report.department === grant.department && report.is_active,
        )
        const groupName = `scope-${grant.department}`
        return (
          <fieldset
            key={grant.department}
            className="rounded-[var(--radius-md)] border border-[var(--color-rule)] p-3"
          >
            <legend className="flex items-center gap-2 px-1 text-[length:var(--text-sm)] font-medium">
              <span
                aria-hidden
                className="h-4 w-0.5 rounded-full"
                style={{ background: department?.accent ?? 'var(--color-ink-muted)' }}
              />
              {department?.name ?? grant.department}
            </legend>

            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-1.5 text-[length:var(--text-sm)]">
                <input
                  type="radio"
                  name={groupName}
                  checked={grant.scope === 'all'}
                  onChange={() => {
                    update(index, { scope: 'all' })
                  }}
                />
                All reports
              </label>
              <label className="flex items-center gap-1.5 text-[length:var(--text-sm)]">
                <input
                  type="radio"
                  name={groupName}
                  checked={grant.scope === 'selected'}
                  onChange={() => {
                    update(index, { scope: 'selected' })
                  }}
                />
                Specific reports
              </label>
              <Button
                variant="danger"
                className="ml-auto"
                onClick={() => {
                  setDraft((current) => current.filter((_unused, position) => position !== index))
                }}
              >
                Remove
              </Button>
            </div>

            {grant.scope === 'all' ? (
              <p className="mt-2 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
                Every report in this department, including ones added later. Reports marked{' '}
                <Badge tone="warning">restricted</Badge> are excluded — they can only be granted
                by name.
              </p>
            ) : (
              <>
                <ul className="mt-2 space-y-1">
                  {inDepartment.map((report) => (
                    <li key={report.slug}>
                      <label className="flex items-center gap-2 text-[length:var(--text-sm)]">
                        <input
                          type="checkbox"
                          checked={grant.slugs.includes(report.slug)}
                          onChange={() => {
                            toggleSlug(index, report.slug)
                          }}
                        />
                        {report.title}
                        <span className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                          {report.slug}
                        </span>
                        {report.restricted && <Badge tone="warning">restricted</Badge>}
                      </label>
                    </li>
                  ))}
                </ul>
                {problems[index] !== undefined && (
                  <p
                    role="alert"
                    className="mt-2 text-[length:var(--text-xs)] text-[var(--color-negative)]"
                  >
                    {problems[index]}
                  </p>
                )}
              </>
            )}
          </fieldset>
        )
      })}

      <div className="flex flex-wrap items-center gap-2">
        {available.length > 0 && (
          <label className="flex items-center gap-2 text-[length:var(--text-xs)]">
            Add department
            <select
              value=""
              aria-label="Add department"
              onChange={(event) => {
                const key = event.target.value
                if (key === '') return
                // `all` is the default, per §7.1: it is the normal case, and
                // the one that does not need an admin edit next month.
                setDraft((current) => [...current, { department: key, scope: 'all', slugs: [] }])
              }}
              className="rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 py-1"
            >
              <option value="">Choose…</option>
              {available.map((department) => (
                <option key={department.key} value={department.key}>
                  {department.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button
          variant="primary"
          className="ml-auto"
          disabled={saving || Object.keys(problems).length > 0}
          onClick={() => {
            onSave(toGrantInputs(draft))
          }}
        >
          {saving ? 'Saving…' : 'Save grants'}
        </Button>
      </div>
      <p className="text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
        Saving replaces this role's grants entirely — anything removed here is revoked.
      </p>
    </div>
  )
}
